import { useDisclosure } from '@mantine/hooks';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useGoal, useGoals, useOutcome, useOutcomes } from '../../../api/strategy-client/strategy-client.hooks';
import { useTranslation } from 'react-i18next';
import { EmptyFilterResult } from '../../../components/filters/empty-filter-result';
import { BrandedLoadingOverlay } from '../../../components/loader/branded-loader';
import { PageLayout } from '../../../components/page-layout/page-layout';
import { useDocumentTitle } from '../../../helpers/general-helpers';
import { useStrategyStore } from '../../../store/strategy-store/strategy-store';
import { MenuDrivenContainer } from '../../menu-driven-container/menu-driven-container';
import { StrategyHeader, StrategyTabsList } from '../strategy-header';
import { StrategyTab } from '../strategy.type';
import { GoalOverviewDetailView } from './components/goal-overview-detail-view';
import { GoalOverviewSimplifiedWidget } from './components/goal-overview-simplified-widget';
import { OutcomeOverviewDetailView } from './components/outcome-overview-detail-view';
import { OutcomeOverviewSimplifiedWidget } from './components/outcome-overview-simplified-widget';
import { OverviewFilters } from './overview-filters';
import { isStrategyEntityWithinCommittedWeeks } from './overview.helpers';

export const StrategyOverview = () => {
  const i18n = useTranslation();
  useDocumentTitle('Overview - Bloomfilter');
  const [searchParams, setSearchParams] = useSearchParams();
  const outcomeId = searchParams.get('outcomeId');
  const goalId = searchParams.get('goalId');

  const overviewFilters = useStrategyStore((state) => state.overviewFilters);
  // Only opt in to include_hidden when the user is actually viewing the Outcomes
  // grid; the toggle UI is hidden in the Goals view, so we ignore the persisted
  // value there to avoid loading hidden Outcomes that nothing renders.
  const includeHiddenOutcomes = overviewFilters.view_mode === 'outcomes' && overviewFilters.mode_show_hidden;
  const { outcomes, query: outcomesQuery } = useOutcomes(undefined, includeHiddenOutcomes);
  const { goals, query: goalsQuery } = useGoals();
  const { outcome: detailOutcome, query: detailOutcomeQuery } = useOutcome(outcomeId || undefined);
  const { goal: detailGoal, query: detailGoalQuery } = useGoal(goalId || undefined);
  const [filtersOpen, filtersCtrl] = useDisclosure(true);
  const [searchTerm, setSearchTerm] = useState('');

  const isLoading = outcomesQuery.isLoading || goalsQuery.isLoading;

  // Create a map of outcome ID -> array of goal IDs (M2M relationship)
  const outcomeToGoalsMap = useMemo(() => {
    const map = new Map<string, string[]>();

    // Initialize map with empty arrays for all outcomes
    outcomes.forEach((outcome) => {
      map.set(outcome.id, []);
    });

    // Iterate through goals and map each outcome to its parent goals
    goals.forEach((goal) => {
      if (goal.outcomes && goal.outcomes.length > 0) {
        goal.outcomes.forEach((outcome) => {
          const existingGoals = map.get(outcome.id) || [];
          map.set(outcome.id, [...existingGoals, goal.id]);
        });
      }
    });

    return map;
  }, [outcomes, goals]);

  // Create a map of goal ID -> array of outcome objects (inverse relationship)
  const goalToOutcomesMap = useMemo(() => {
    const map = new Map<string, typeof outcomes>();

    // Initialize map with empty arrays for all goals
    goals.forEach((goal) => {
      map.set(goal.id, []);
    });

    // Map goals to their outcomes
    goals.forEach((goal) => {
      if (goal.outcomes && goal.outcomes.length > 0) {
        map.set(goal.id, goal.outcomes);
      }
    });

    return map;
  }, [goals]);

  // Filter outcomes by their own status and bookmarked status
  const filteredOutcomes = useMemo(() => {
    return outcomes.filter((outcome) => {
      // Filter by include empty (show only outcomes with initiatives when OFF)
      if (!overviewFilters.mode_include_empty) {
        if (!outcome.initiatives || outcome.initiatives.length === 0) {
          return false;
        }
      }

      // Filter by search term (name, key, or title)
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesName = outcome.name?.toLowerCase().includes(searchLower);
        const matchesKey = outcome.key?.toLowerCase().includes(searchLower);

        if (!matchesName && !matchesKey) {
          return false;
        }
      }

      // Filter by objective status
      if (overviewFilters.selected_statuses?.length > 0) {
        const outcomeStatus = outcome.status?.by_tasks?.status || 'unknown';
        if (!overviewFilters.selected_statuses.includes(outcomeStatus)) {
          return false;
        }
      }

      // Filter by bookmarked status
      if (overviewFilters.mode_bookmarked && !outcome.is_bookmarked) {
        return false;
      }

      // Filter by committed next weeks
      if (overviewFilters.mode_committed) {
        if (!isStrategyEntityWithinCommittedWeeks(outcome, overviewFilters.committed_next_weeks_value)) {
          return false;
        }
      }

      return true;
    });
  }, [outcomes, overviewFilters, searchTerm]);

  // Filter goals by their own status and bookmarked status
  const filteredGoals = useMemo(() => {
    return goals.filter((goal) => {
      // Filter by include empty (show only goals with outcomes when OFF)
      if (!overviewFilters.mode_include_empty) {
        if (!goal.outcomes || goal.outcomes.length === 0) {
          return false;
        }
      }

      // Filter by search term (name or key)
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesName = goal.name?.toLowerCase().includes(searchLower);
        const matchesKey = goal.key?.toLowerCase().includes(searchLower);

        if (!matchesName && !matchesKey) {
          return false;
        }
      }

      // Filter by objective status
      if (overviewFilters.selected_statuses?.length > 0) {
        const goalStatus = goal.status?.by_tasks?.status || 'unknown';
        if (!overviewFilters.selected_statuses.includes(goalStatus)) {
          return false;
        }
      }

      // Filter by bookmarked status
      if (overviewFilters.mode_bookmarked && !goal.is_bookmarked) {
        return false;
      }

      // Filter by committed next weeks (using same helper as outcomes)
      if (overviewFilters.mode_committed) {
        if (!isStrategyEntityWithinCommittedWeeks(goal, overviewFilters.committed_next_weeks_value)) {
          return false;
        }
      }

      return true;
    });
  }, [goals, overviewFilters, searchTerm]);

  const viewMode = overviewFilters.view_mode;

  // Handler to go back to overview from detail view
  const handleBackToOverview = () => {
    setSearchParams({});
  };

  // If showing outcome detail view
  if (outcomeId) {
    const outcomeGoalIds = outcomeToGoalsMap.get(outcomeId) || [];
    const outcomeGoals = goals.filter((goal) => outcomeGoalIds.includes(goal.id));

    return (
      <MenuDrivenContainer header={<StrategyHeader />}>
        <BrandedLoadingOverlay visible={detailOutcomeQuery.isLoading} variant="colored" />
        <PageLayout
          controlBar={<StrategyTabsList active={StrategyTab.Overview} />}
          mainSection={
            detailOutcome && (
              <OutcomeOverviewDetailView outcome={detailOutcome} goals={outcomeGoals} onBack={handleBackToOverview} />
            )
          }
        />
      </MenuDrivenContainer>
    );
  }

  // If showing goal detail view
  if (goalId) {
    return (
      <MenuDrivenContainer header={<StrategyHeader />}>
        <BrandedLoadingOverlay visible={detailGoalQuery.isLoading} variant="colored" />
        <PageLayout
          controlBar={<StrategyTabsList active={StrategyTab.Overview} />}
          mainSection={detailGoal && <GoalOverviewDetailView goal={detailGoal} onBack={handleBackToOverview} />}
        />
      </MenuDrivenContainer>
    );
  }

  return (
    <MenuDrivenContainer header={<StrategyHeader />}>
      <BrandedLoadingOverlay visible={isLoading} variant="colored" />
      <PageLayout
        controlBar={<StrategyTabsList active={StrategyTab.Overview} />}
        mainSectionBackgroundColor="transparent"
        sideFilters={{
          title: `${i18n.t('common.filters')}`,
          content: (
            <OverviewFilters
              setShow={(show) => (show ? filtersCtrl.open() : filtersCtrl.close())}
              searchTerm={searchTerm}
              setSearchTerm={setSearchTerm}
            />
          ),
          show: filtersOpen,
          setShow: (show) => (show ? filtersCtrl.open() : filtersCtrl.close()),
        }}
        mainSection={
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: '24px',
              height: '100%',
              width: '100%',
              overflowY: 'auto',
              alignContent: 'start',
              borderRadius: '8px',
            }}
          >
            {viewMode === 'outcomes'
              ? // Outcomes View
                filteredOutcomes.length === 0
                ? !isLoading && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        minHeight: '260px',
                        gridColumn: '1 / -1',
                      }}
                    >
                      <EmptyFilterResult objectName={i18n.t('common.outcomes')} />
                    </div>
                  )
                : filteredOutcomes.map((outcome) => {
                    const goalIds = outcomeToGoalsMap.get(outcome.id) || [];
                    const outcomeGoals = goals.filter((goal) => goalIds.includes(goal.id));

                    // Filter initiatives by is_active when toggle is ON
                    const filteredInitiatives = overviewFilters.mode_is_active_only
                      ? (outcome.initiatives || []).filter((initiative) => initiative.is_active)
                      : outcome.initiatives || [];

                    return (
                      <OutcomeOverviewSimplifiedWidget
                        key={outcome.id}
                        outcome={outcome}
                        goals={outcomeGoals}
                        initiatives={filteredInitiatives}
                      />
                    );
                  })
              : // Goals View
                filteredGoals.length === 0
                ? !isLoading && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        minHeight: '260px',
                        gridColumn: '1 / -1',
                      }}
                    >
                      <EmptyFilterResult objectName={i18n.t('common.strategic_goals')} />
                    </div>
                  )
                : filteredGoals.map((goal) => {
                    const goalOutcomes = goalToOutcomesMap.get(goal.id) || [];

                    // Filter outcomes by is_active when toggle is ON
                    const filteredGoalOutcomes = overviewFilters.mode_is_active_only
                      ? goalOutcomes.filter((outcome) => outcome.is_active)
                      : goalOutcomes;

                    return <GoalOverviewSimplifiedWidget key={goal.id} goal={goal} outcomes={filteredGoalOutcomes} />;
                  })}
          </div>
        }
      />
    </MenuDrivenContainer>
  );
};
